import { FloatingPortal, offset, size, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { graphql } from 'app/generated/gql'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { CustomQuestionType, LeadOverrideConfigType, SchedulingConfigurationJson } from 'app/types/entities'
import { toSchedulingConfigurationInput, toSchedulingConfigurationJson } from 'app/lib/schedulingConfiguration'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useCopyToClipboard } from 'app/hooks/useCopyToClipboard'

const ONLINE_SCHEDULER_LOCATION_QUERY = graphql(`
  query OnlineSchedulerLocation($locationId: ID!) {
    location(id: $locationId) {
      id
      name
      isSchedulerLive
    }
    schedulingConfiguration(locationId: $locationId) {
      id
      locationId
      allowDoubleBooking
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
      customQuestions {
        id
        fieldName
        required
        fieldType
        enabled
        description
        marketingConsent
        optIn
      }
      leadOverrideEnabled
      leadOverrideConfig {
        amount
        unit
      }
    }
  }
`)

const UPDATE_SCHEDULING_CONFIGURATION_MUTATION = graphql(`
  mutation UpdateOnlineSchedulerConfig($locationId: ID!, $input: UpdateSchedulingConfigurationInput!) {
    updateSchedulingConfiguration(locationId: $locationId, input: $input) {
      id
      locationId
      allowDoubleBooking
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
      customQuestions {
        id
        fieldName
        required
        fieldType
        enabled
        description
        marketingConsent
        optIn
      }
      leadOverrideEnabled
      leadOverrideConfig {
        amount
        unit
      }
    }
  }
`)

const TOGGLE_SCHEDULER_LIVE_MUTATION = graphql(`
  mutation ToggleOnlineSchedulerLive($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      id
      isSchedulerLive
    }
  }
`)

export type UseOnlineSchedulerLocationReturn = {
  location: any
  schedulingConfiguration: SchedulingConfigurationJson | undefined
  customQuestions: CustomQuestionType[]
  showQuestionPanel: boolean
  setShowQuestionPanel: (v: boolean) => void
  showLeadOverride: boolean
  setShowLeadOverride: (v: boolean) => void
  isDirtyForm: boolean
  locationId: string | undefined
  placement: `bottom-start` | `top-start`
  floatingData: ReturnType<typeof useFloating>
  floatingDataLeadOverride: ReturnType<typeof useFloating>
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps']
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps']
  getLeadOverrideReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps']
  getLeadOverrideFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps']
  saveRequest: { inFlight: boolean }
  onToggleSchedulerLive: (value: boolean) => Promise<boolean>
  copyToClipboard: ReturnType<typeof useCopyToClipboard>
  schedulingUrl: string
  handleSaveConfiguration: () => Promise<void>
  onMoveQuestion: (dragIndex: number, hoverIndex: number) => void
  onDropQuestion: () => Promise<void>
  onQuestionCreateCompleted: (question: CustomQuestionType) => Promise<void>
  onQuestionUpdateCompleted: (question: CustomQuestionType) => void
  onQuestionDeleted: (id: string) => void
  onLeadOverrideSaved: (data: { enabled: boolean; config: LeadOverrideConfigType }) => void
}

export function useOnlineSchedulerLocation(): UseOnlineSchedulerLocationReturn {
  const { locationId } = useParams()
  const [location, setLocation] = useState<any>({})
  const [schedulingConfiguration, setSchedulingConfiguration] = useState<SchedulingConfigurationJson | undefined>()
  const [customQuestions, setCustomQuestions] = useState<CustomQuestionType[]>([])
  const [showQuestionPanel, setShowQuestionPanel] = useState(false)
  const [showLeadOverride, setShowLeadOverride] = useState(false)
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [placement, setPlacement] = useState<`bottom-start` | `top-start`>(`bottom-start`)
  const [saveInFlight, setSaveInFlight] = useState(false)
  const saveRequest = { inFlight: saveInFlight }
  const [confirm] = useConfirmation()

  const [result] = useQuery({
    query: ONLINE_SCHEDULER_LOCATION_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })

  useEffect(() => {
    if (result.data?.location) {
      setLocation(result.data.location)
    }
    if (result.data?.schedulingConfiguration) {
      const sc = toSchedulingConfigurationJson(result.data.schedulingConfiguration)
      setSchedulingConfiguration(sc)
      setCustomQuestions(sc.customQuestions ?? [])
    }
  }, [result.data])

  const [, executeSaveConfig] = useMutation(UPDATE_SCHEDULING_CONFIGURATION_MUTATION)
  const [, executeToggle] = useMutation(TOGGLE_SCHEDULER_LIVE_MUTATION)

  const PANEL_MAX_HEIGHT = 584

  const floatingData = useFloating({
    placement,
    middleware: [
      offset(8),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${PANEL_MAX_HEIGHT}px`,
            width: `${rects.reference.width}px`,
            minWidth: `450px`,
            overflowY: `auto`,
          })
        },
        padding: 10,
      }),
    ],
    open: showQuestionPanel,
    onOpenChange: setShowQuestionPanel,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const floatingDataLeadOverride = useFloating({
    placement: `bottom-start`,
    middleware: [
      offset(8),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            width: `${rects.reference.width}px`,
            minWidth: `450px`,
          })
        },
        padding: 10,
      }),
    ],
    open: showLeadOverride,
    onOpenChange: setShowLeadOverride,
  })
  const { getReferenceProps: getLeadOverrideReferenceProps, getFloatingProps: getLeadOverrideFloatingProps } =
    useInteractions([useClick(floatingDataLeadOverride.context), useDismiss(floatingDataLeadOverride.context)])

  useEffect(() => {
    if (showQuestionPanel) {
      const referenceElement = floatingData.refs.reference.current
      if (referenceElement) {
        const rect = (referenceElement as Element).getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom - 10
        const spaceAbove = rect.top - 10
        if (spaceBelow >= PANEL_MAX_HEIGHT) {
          setPlacement(`bottom-start`)
        } else if (spaceAbove >= PANEL_MAX_HEIGHT) {
          setPlacement(`top-start`)
        } else {
          setPlacement(spaceBelow > spaceAbove ? `bottom-start` : `top-start`)
        }
      }
    }
  }, [showQuestionPanel, floatingData.refs.reference])

  const onToggleSchedulerLive = async (value: boolean) => {
    return confirm({
      title: `Change Scheduler Live Status`,
      description: value
        ? `Are you sure you want to turn on the Scheduler?`
        : `Are you sure you want to disable the Scheduler?`,
      variant: `danger`,
    })
      .then(async () => {
        const res = await executeToggle({ id: locationId!, input: { isSchedulerLive: value } })
        if (!res.error) {
          notifySuccess(`Scheduler status updated`)
          return true
        } else {
          notifyError(`Scheduler live status could not be updated`)
          return false
        }
      })
      .catch(() => false)
  }

  const copyToClipboard = useCopyToClipboard()
  const schedulingUrl = `${window.location.origin}/scheduling/${location.id}`

  const handleSaveConfiguration = async () => {
    setSaveInFlight(true)
    const result = await executeSaveConfig({
      locationId: locationId!,
      input: toSchedulingConfigurationInput(schedulingConfiguration, customQuestions),
    })
    setSaveInFlight(false)

    if (result.error) {
      notifyError(`Failed to update configuration`)
    } else if (result.data?.updateSchedulingConfiguration) {
      const sc = toSchedulingConfigurationJson(result.data.updateSchedulingConfiguration)
      setSchedulingConfiguration(sc)
      setCustomQuestions(sc.customQuestions ?? [])
      notifySuccess(`Configuration saved successfully`)
      setIsDirtyForm(false)
    }
  }

  const onMoveQuestion = (dragIndex: number, hoverIndex: number) => {
    const dragQuestion = customQuestions[dragIndex]
    setCustomQuestions((prev) => {
      const updatedQuestions = [...prev]
      updatedQuestions.splice(dragIndex, 1)
      updatedQuestions.splice(hoverIndex, 0, dragQuestion)
      return updatedQuestions
    })
    setIsDirtyForm(true)
  }

  const onDropQuestion = async () => setIsDirtyForm(true)

  const onQuestionCreateCompleted = async (question: CustomQuestionType) => {
    setShowQuestionPanel(false)
    setCustomQuestions((prev) => [...prev, question])
    setIsDirtyForm(true)
  }

  const onQuestionUpdateCompleted = (question: CustomQuestionType) => {
    const index = customQuestions.findIndex((q) => q.id === question.id)
    if (index !== -1) {
      setCustomQuestions((prev) => {
        const updatedQuestions = [...prev]
        updatedQuestions[index] = question
        return updatedQuestions
      })
      setIsDirtyForm(true)
    }
  }

  const onQuestionDeleted = (id: string) => {
    setCustomQuestions((prev) => prev.filter((q) => q.id !== id))
    setIsDirtyForm(true)
  }

  const onLeadOverrideSaved = (data: { enabled: boolean; config: LeadOverrideConfigType }) => {
    setShowLeadOverride(false)
    setSchedulingConfiguration({
      ...schedulingConfiguration,
      leadOverrideEnabled: data.enabled,
      leadOverrideConfig: data.config,
    })
    setIsDirtyForm(true)
  }

  return {
    location,
    schedulingConfiguration,
    customQuestions,
    showQuestionPanel,
    setShowQuestionPanel,
    showLeadOverride,
    setShowLeadOverride,
    isDirtyForm,
    locationId,
    placement,
    floatingData,
    floatingDataLeadOverride,
    getReferenceProps,
    getFloatingProps,
    getLeadOverrideReferenceProps,
    getLeadOverrideFloatingProps,
    saveRequest,
    onToggleSchedulerLive,
    copyToClipboard,
    schedulingUrl,
    handleSaveConfiguration,
    onMoveQuestion,
    onDropQuestion,
    onQuestionCreateCompleted,
    onQuestionUpdateCompleted,
    onQuestionDeleted,
    onLeadOverrideSaved,
  }
}
